// @flow

import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import * as R from 'ramda';
import debounce from 'lodash.debounce';
import { log } from '../../utils/jsUtils';
import { useSelector, useDispatch } from 'react-redux';
import SPBackend from '../../services/SPBackend';

import TenantsHeader from './TenantsHeader';
import TenantsContent from './TenantsContent';
import MemberDrawer from '../MemberDrawer/index';

import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { createClasses } from '../../utils/stylesUtils';
import styles from './Styles/TenantsHome.Style';

// Import the new tenants API hook
import { useGetTenantsQuery, tenantsApi } from '../../features/api/tenantsApi';

// Actions
import { 
  setIsAppLoading, 
  setLoadingModule, 
  setSnackMessage 
} from '../../reduxStore/modules/common/actions';
import { setTenantEnableUpload, setTenants } from '../../reduxStore/modules/tenants/actions';
import { ACTION_SET_SORT_ORDER, ACTION_SET_SORT_BY } from '../../reduxStore/modules/tenants/types';

// Selectors
import { selectUser } from '../../reduxStore/modules/user/selectors';
import {
  selectTenants,
  selectTenantEnableUpload,
  selectUserExplicitlyEnabledUpload,
  selectTenantsPageInfo
} from '../../reduxStore/modules/tenants/selectors';
import { useI18n } from '../../contexts/I18nContext';

type Props = {
  classes: Object
};

const TenantsHome = () => {
  const theme = useTheme();
  const classes = createClasses(styles, theme);
  
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const i18n = useI18n();
  
  // Get tenantsPageInfo from Redux using memoized selector
  const tenantsPageInfo = useSelector(selectTenantsPageInfo);
  const tenantEnableUpload = useSelector(selectTenantEnableUpload);
  const userExplicitlyEnabledUpload = useSelector(selectUserExplicitlyEnabledUpload);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [customerIdsSelected, setCustomerIdsSelected] = useState([]);
  const [tenantsNextPage, setTenantsNextPage] = useState(null);
  const [countOfTenants, setCountOfTenants] = useState(0);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);

  const [searchTenantValue, setSearchValue] = useState('');

  // Use the RTK Query hook to fetch tenants
  const {
    data: tenantsData,
    isLoading: isTenantsLoading,
    isFetching: isTenantsRefetching,
    refetch: refetchTenants
  } = useGetTenantsQuery({ 
    searchVal: searchTenantValue, 
    tenantsPageInfo, 
    user 
  }, {
    // Only run the query if we have a user
    skip: !user,
    // Keep previous data until the next successful response
    refetchOnMountOrArgChange: true
  });

  // Get tenants from Redux state 
  const tenants = useSelector(selectTenants);

  // Update Redux state when RTK Query data changes
  useEffect(() => {
    if (tenantsData) {
      // Mark initial load as complete
      setHasInitiallyLoaded(true);
      // Update tenants in Redux state
      dispatch(setTenants(tenantsData.tenants));
      // Update other related state
      setTenantsNextPage(tenantsData.tenantsNextPage);
      setCountOfTenants(tenantsData.tenantsCount);

      // Auto-hide the upload UI if we have tenants (but only if user didn't explicitly enable it)
      if (tenantsData.tenants.length > 0 && tenantEnableUpload && !userExplicitlyEnabledUpload) {
        dispatch(setTenantEnableUpload(false, false)); // false = not a user action
      } else if (tenantsData.tenants.length === 0 && !tenantEnableUpload) {
        // Auto-show the upload UI if we don't have tenants
        dispatch(setTenantEnableUpload(true, false)); // false = not a user action
      }
    }
  }, [tenantsData, dispatch, tenantEnableUpload, userExplicitlyEnabledUpload]);

  // Set loading status based on RTK Query status
  // Only dispatch when user is available (query is not skipped)
  useEffect(() => {
    if (user) {
      dispatch(setIsAppLoading(isTenantsLoading || isTenantsRefetching));
    }
  }, [isTenantsLoading, isTenantsRefetching, dispatch, user]);

  const removeTenant = (tenant) => {
    if (user !== null) {
      dispatch(setLoadingModule('removeTenant', true));
      
      SPBackend.removeTenant(tenant._id, user)
      .then(response => {
        const filterById = p => p._id !== tenant._id;
        dispatch(setTenants(R.filter(filterById, tenants)));
        log('[TenantsHome] tenants, response: ', tenants, response);
        // Show snack message
        dispatch(setSnackMessage({
          message: `${i18n.t('Tenant')} Removed`,
          level: 'success',
          duration: 3000
        }));
        dispatch(setLoadingModule('removeTenant', false));
      })
      .catch(error => {
        log('[TenantsHome.fetchFeed.getFeed] error: ', error);
        dispatch(setSnackMessage({
          message: 'An error occured. Please try again.',
          level: 'error',
          duration: 3000
        }));
        dispatch(setLoadingModule('removeTenant', false));
      });
    }
  };

  const usehandleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSetSearchInputValue = debounce(value => {
    setSearchValue(value);
  });

  /**
   * 
   * @param {*} prop 
   * @param {*} direction 
   * @param {*} tenantsToSet overrides the state list of tenants
   */
  const orderTenants = (order: number, byProp: Array<string>) => {
    log('[TenantsHome] orderby', order, byProp);
    if (typeof order !== 'undefined' && order !== null && order !== 0){
      dispatch({
        type: ACTION_SET_SORT_ORDER,
        sortOrder: order
      });
      dispatch({
        type: ACTION_SET_SORT_BY,
        sortBy: byProp
      });
    }
  };

  // This function is no longer needed with RTK Query
  // Keeping it as a stub for backward compatibility
  const refreshTenants = (searchVal: string) => {
    log('[refreshTenants]... - using RTK Query now');
    // This will trigger a re-render because searchTenantValue changes,
    // which will cause the RTK Query to re-fetch
    if (searchVal !== searchTenantValue) {
      setSearchValue(searchVal);
    }
  };

  const updateTenants = (tenantIds, tag) => {
    log('[UPDATING TENANTS]', tenantIds);
    const updatedTenants = tenants.map(tenant => {
      if (tenantIds.includes(tenant._id)) {
        log('[SELECTED TENANT]', tenant);
        // Create a new object to ensure Redux detects the change
        return {
          ...tenant,
          Tags: tag ? R.union(tenant.Tags || [], [tag._id]) : null
        };
      }
      return tenant;
    });
    
    // Update Redux store
    dispatch(setTenants(updatedTenants));
  };

  const isCustomerSelected = (id) => customerIdsSelected.indexOf(id) !== -1;

  // Handle import complete - refresh tenant list
  const handleImportComplete = () => {
    log('[TenantsHome] Import complete - refreshing tenants list');
    // Invalidate cache and trigger explicit refetch for reliability
    dispatch(tenantsApi.util.invalidateTags(['Tenants']));
    refetchTenants();
  };

  const handleSelectCustomer = (event, customerId) => {
    const selectedIndex = customerIdsSelected.indexOf(customerId);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(customerIdsSelected, customerId);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(customerIdsSelected.slice(1));
    } else if (selectedIndex === customerIdsSelected.length - 1) {
      newSelected = newSelected.concat(customerIdsSelected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        customerIdsSelected.slice(0, selectedIndex),
        customerIdsSelected.slice(selectedIndex + 1),
      );
    }
    setCustomerIdsSelected(newSelected);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = tenants.map((t) => t._id);
      setCustomerIdsSelected(newSelecteds);
      return;
    }
    setCustomerIdsSelected([]);
  };

  return (
    <Box sx={classes.root}>
      <Helmet>
        <title>Neo | Customers</title>
      </Helmet>
      <TenantsHeader
        customerIdsSelected={customerIdsSelected}
        // searchInputValue={searchTenantValue}
        handleSetSearchInputValue={handleSetSearchInputValue}
        mobileOpen={mobileOpen}
        orderTenants={orderTenants}
        refreshTenants={refreshTenants}
        searchTenantValue={searchTenantValue}
        setCountOfTenants={setCountOfTenants} 
        setTenants={setTenants}
        tenants={tenants}
        totalTenantsCount={countOfTenants}
        updateTenants={updateTenants}
        usehandleDrawerToggle={usehandleDrawerToggle}
      />
      <MemberDrawer 
        mobileOpen={mobileOpen} 
        usehandleDrawerToggle={usehandleDrawerToggle}
      />
      <TenantsContent
        handleSelectAllClick={handleSelectAllClick}
        handleSelectCustomer={handleSelectCustomer}
        hasInitiallyLoaded={hasInitiallyLoaded}
        isCustomerSelected={isCustomerSelected}
        orderTenants={orderTenants}
        removeTenant={removeTenant}
        selectedRowsCount={customerIdsSelected.length}
        setCountOfTenants={setCountOfTenants}
        setTenants={setTenants}
        // sort={currentSort}
        tenants={tenants}
        tenantsNextPage={tenantsNextPage}
        totalTenantsCount={countOfTenants}
        onImportComplete={handleImportComplete}
      /> 
    </Box>
  );
};

export default TenantsHome;
